import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { APPLY_PROFILE_UPDATE_ENDPOINT, CHAT_ENDPOINT, getAuthHeaders, supabase } from '../lib/supabase';
import Nav from '../components/Nav';
import GainsLogo from '../components/GainsLogo';
import ProfileBanner from '../components/ProfileBanner';
import { formatMarkdown } from '../lib/formatMarkdown';
import { OfflineMessage } from '../components/OfflineBanner';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { Clock, Bookmark, ThumbsUp, ThumbsDown, UserRound } from 'lucide-react';
import { cacheGet, cacheSet, chatHistoryKey } from '../lib/offlineCache';
import { pickNudgeTemplate, type ProfileSection } from '../lib/chatNudgeTemplates';
import CoachContactModal from '../components/CoachContactModal';
import ProfileUpdateCard, { type ProfileProposalData, type ProfileCardStatus } from '../components/ProfileUpdateCard';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  /** When set, this message renders as a profile-update card, not a bubble. */
  profileProposal?: ProfileProposalData;
  proposalStatus?: ProfileCardStatus;
  sources?: { title: string; author: string; source: string }[];
  message_id?: string;
  bookmarked?: boolean;
  rating?: 1 | -1 | null;
  summary?: string;
  summarizing?: boolean;
  streaming?: boolean;
}



export default function ChatPage({ session }: { session: Session }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(20);
  const [tier, setTier] = useState<'free' | 'paid'>('free');
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [freeLimit, setFreeLimit] = useState(3);
  const [tierLoaded, setTierLoaded] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const online = useOnlineStatus();
  const [showCoachModal, setShowCoachModal] = useState(false);

  // Load cached messages when offline
  useEffect(() => {
    if (!online && messages.length === 0) {
      cacheGet<Message[]>(chatHistoryKey(session.user.id)).then(cached => {
        if (cached) setMessages(cached);
      });
    }
  }, [online]);

  // Refresh ratings from server for any messages we know about. Runs whenever
  // the set of message_ids changes (e.g. after cached messages load, or after
  // a new assistant message gets its message_id from the streaming "done"
  // event). Cached state can be stale; the DB is the source of truth.
  useEffect(() => {
    if (!online) return;
    const ids = messages.filter(m => m.message_id).map(m => m.message_id!) as string[];
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('chat_message_ratings')
        .select('message_id, rating')
        .eq('user_id', session.user.id)
        .in('message_id', ids);
      if (cancelled || !data) return;
      const byId = new Map<string, 1 | -1>(
        (data as { message_id: string; rating: 1 | -1 }[]).map(r => [r.message_id, r.rating])
      );
      setMessages(prev => prev.map(m => {
        if (!m.message_id) return m;
        const next = byId.get(m.message_id) ?? null;
        return m.rating === next ? m : { ...m, rating: next };
      }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, messages.map(m => m.message_id || '').join(',')]);

  // Cache messages whenever they change (skip empty/streaming)
  useEffect(() => {
    const settled = messages.filter(m => !m.streaming);
    if (settled.length > 0) {
      cacheSet(chatHistoryKey(session.user.id), settled.slice(-20));
    }
  }, [messages]);

  // Load subscription tier and usage on mount
  useEffect(() => {
    (async () => {
      const [{ data: profile }, { data: entitlements }] = await Promise.all([
        supabase.from('profiles').select('role').eq('id', session.user.id).single(),
        supabase.from('user_entitlements').select('id')
          .eq('user_id', session.user.id)
          .eq('feature', 'ai_chat')
          .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
          .limit(1),
      ]);

      const isPaid = profile?.role === 'admin' || (entitlements && entitlements.length > 0);
      setTier(isPaid ? 'paid' : 'free');

      if (!isPaid) {
        const { count } = await supabase
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        setTotalQuestions(count || 0);
      }
      setTierLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, isLoading]);

  const isPaywalled = tier === 'free' && totalQuestions >= freeLimit;

  const maybeAppendNudge = async (assistantIdx: number, question: string, answer: string) => {
    if (!answer || answer.startsWith('Error:')) return;
    try {
      const { data, error } = await supabase.functions.invoke('chat-nudge-classify', {
        body: { question, answer },
      });
      if (error || !data || !data.should_nudge) return;
      const sections = (data.missing_relevant_sections || []) as ProfileSection[];
      const template = pickNudgeTemplate(sections);
      if (!template) return;
      setMessages(prev => prev.map((m, i) =>
        i === assistantIdx ? { ...m, content: m.content + '\n\n' + template } : m
      ));
    } catch {
      // Silent failure — no nudge shown.
    }
  };

  const sendMessage = async (text?: string) => {
    const question = text || input.trim();
    if (!question || isLoading || isPaywalled) return;
    setInput('');
    const userMsg: Message = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    // Add a placeholder assistant message for streaming
    const assistantIdx = messages.length + 1; // index of the new assistant message
    setMessages(prev => [...prev, { role: 'assistant', content: '', sources: [], streaming: true }]);

    try {
      const resp = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ question, history: [...messages, userMsg].slice(-10) }),
      });

      const contentType = resp.headers.get('Content-Type') || '';

      // Handle error responses
      if (!resp.ok) {
        const data = await resp.json();
        if (resp.status === 402 && data.code === 'FREE_LIMIT') {
          setTotalQuestions(freeLimit);
          throw new Error("You've used your 3 free questions");
        }
        throw new Error(data.error || 'Something went wrong');
      }

      // Handle legacy non-streaming JSON response (old edge function)
      if (contentType.includes('application/json')) {
        const data = await resp.json();
        setMessages(prev => prev.map((m, i) =>
          i === assistantIdx
            ? { ...m, content: data.answer, sources: data.sources, message_id: data.message_id, bookmarked: false, rating: null, streaming: false }
            : m
        ));
        if (data.usage) {
          if (data.usage.tier === 'free') {
            setTotalQuestions(data.usage.total_questions);
            setFreeLimit(data.usage.free_limit);
          } else {
            setDailyUsage(data.usage.daily_questions);
            setDailyLimit(data.usage.daily_limit);
          }
        }
        maybeAppendNudge(assistantIdx, question, data.answer);
      } else {
        // Handle streaming SSE response
        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedContent = '';
        let sources: { title: string; author: string; source: string }[] = [];

        // The coach may split the turn around a proposal card: text so far is
        // finalized, the card lands, and the wrap-up streams into a FRESH
        // bubble — so the index the deltas write to can move mid-stream.
        let currentIdx = assistantIdx;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            try {
              const event = JSON.parse(raw);

              if (event.type === 'sources') {
                sources = event.sources || [];
              }

              if (event.type === 'delta') {
                accumulatedContent += event.text;
                setMessages(prev => prev.map((m, i) =>
                  i === currentIdx ? { ...m, content: accumulatedContent } : m
                ));
              }

              if (event.type === 'profile_proposal' && event.proposal_id) {
                const card: Message = {
                  role: 'assistant',
                  content: '',
                  profileProposal: {
                    proposal_id: event.proposal_id,
                    rationale: event.rationale || '',
                    current: event.current,
                    proposal: event.proposal,
                  },
                  proposalStatus: 'pending',
                };
                const closedContent = accumulatedContent;
                accumulatedContent = '';
                setMessages(prev => {
                  const updated = prev.map((m, i) =>
                    i === currentIdx ? { ...m, content: closedContent, streaming: false } : m
                  ).filter((_m, i) => !(i === currentIdx && !closedContent.trim()));
                  updated.push(card);
                  updated.push({ role: 'assistant', content: '', streaming: true });
                  currentIdx = updated.length - 1;
                  return updated;
                });
              }

              if (event.type === 'done') {
                setMessages(prev => prev.map((m, i) =>
                  i === currentIdx
                    ? { ...m, content: accumulatedContent, sources, message_id: event.message_id, bookmarked: false, rating: null, streaming: false }
                    : m
                ));
                if (event.usage) {
                  if (event.usage.tier === 'free') {
                    setTotalQuestions(event.usage.total_questions);
                    setFreeLimit(event.usage.free_limit);
                  } else {
                    setDailyUsage(event.usage.daily_questions);
                    setDailyLimit(event.usage.daily_limit);
                  }
                }
              }

              if (event.type === 'error') {
                throw new Error(event.error || 'Stream error');
              }
            } catch (parseErr) {
              // Skip unparseable SSE lines unless it's a rethrown error
              if (parseErr instanceof Error && parseErr.message !== 'Stream error') continue;
              throw parseErr;
            }
          }
        }

        // Ensure streaming flag is cleared even if no 'done' event arrived
        setMessages(prev => prev.map((m, i) =>
          i === currentIdx && m.streaming ? { ...m, sources, streaming: false } : m
        ));

        maybeAppendNudge(assistantIdx, question, accumulatedContent);
      }

    } catch (err: any) {
      setMessages(prev => {
        // Replace the streaming placeholder with an error, or append one
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: 'Error: ' + err.message, sources: [], streaming: false } : m
          );
        }
        return [...prev, { role: 'assistant', content: 'Error: ' + err.message, sources: [] }];
      });
    }
    setIsLoading(false);
    inputRef.current?.focus();
  };

  const toggleBookmark = async (msgId: string, idx: number) => {
    const msg = messages[idx];
    if (!msg || !msgId) return;
    if (msg.bookmarked) {
      await supabase.from('bookmarks').delete().eq('user_id', session.user.id).eq('message_id', msgId);
    } else {
      await supabase.from('bookmarks').insert({ user_id: session.user.id, message_id: msgId });
    }
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, bookmarked: !m.bookmarked } : m));
  };

  const setRating = async (msgId: string, idx: number, value: 1 | -1) => {
    const msg = messages[idx];
    if (!msg || !msgId) return;
    const previous = msg.rating ?? null;
    const next = previous === value ? null : value;
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, rating: next } : m));
    try {
      if (next === null) {
        await supabase.from('chat_message_ratings')
          .delete()
          .eq('user_id', session.user.id)
          .eq('message_id', msgId);
      } else {
        await supabase.from('chat_message_ratings')
          .upsert(
            { user_id: session.user.id, message_id: msgId, rating: next },
            { onConflict: 'message_id,user_id' }
          );
      }
    } catch {
      setMessages(prev => prev.map((m, i) => i === idx ? { ...m, rating: previous } : m));
    }
  };

  const summarizeMessage = async (msgId: string, idx: number) => {
    const msg = messages[idx];
    if (!msg || !msgId || msg.summary || msg.summarizing) return;
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, summarizing: true } : m));
    try {
      const { data, error } = await supabase.functions.invoke('summarize', {
        body: { message_id: msgId },
      });
      if (error) throw new Error(error.message || 'Failed to summarize');
      if (data?.error) throw new Error(data.error || 'Failed to summarize');
      setMessages(prev => prev.map((m, i) => i === idx ? { ...m, summary: data?.summary, summarizing: false } : m));
    } catch {
      setMessages(prev => prev.map((m, i) => i === idx ? { ...m, summarizing: false } : m));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };



  const usagePill = tier === 'free'
    ? <div className="usage-pill">{totalQuestions}/{freeLimit} free</div>
    : <div className="usage-pill">{dailyUsage}/{dailyLimit}</div>;

  return (
    <div className="app-layout">
      <Nav isOpen={navOpen} onClose={() => setNavOpen(false)} />
      <div className="main-content">
        <ProfileBanner userId={session.user.id} />
        <header className="chat-header">
          <button className="menu-btn" onClick={() => setNavOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          </button>
          {tierLoaded && usagePill}
          <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
            <button onClick={() => navigate('/history')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-dim)' }} title="History">
              <Clock size={20} />
            </button>
            <button onClick={() => navigate('/bookmarks')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-dim)' }} title="Bookmarks">
              <Bookmark size={20} />
            </button>
          </div>
        </header>

        {messages.length === 0 && !isLoading ? (
          <div className="welcome">
            <GainsLogo className="welcome-gains-logo" />
            <h2>The AI that speaks your language.</h2>
            {!isPaywalled && (
              <div style={{ marginTop: 8 }} />
            )}
            {isPaywalled && (
              <div className="paywall-card">
                <div className="paywall-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                </div>
                <h3>You've used your 3 free questions</h3>
                <p>Upgrade to get unlimited access to the full coaching knowledge base.</p>
                <button className="paywall-btn" onClick={() => navigate('/checkout')}>Upgrade Now</button>
              </div>
            )}
          </div>
        ) : (
          <div className="messages" ref={messagesRef}>
            {messages.map((m, i) => m.profileProposal ? (
              <ProfileUpdateCard
                key={i}
                data={m.profileProposal}
                status={m.proposalStatus ?? 'pending'}
                onResolve={async (action) => {
                  const pid = m.profileProposal!.proposal_id;
                  setMessages(prev => prev.map(msg =>
                    msg.profileProposal?.proposal_id === pid
                      ? { ...msg, proposalStatus: action === 'apply' ? 'applying' : msg.proposalStatus }
                      : msg
                  ));
                  try {
                    const resp = await fetch(APPLY_PROFILE_UPDATE_ENDPOINT, {
                      method: 'POST',
                      headers: await getAuthHeaders(),
                      body: JSON.stringify({ proposal_id: pid, action }),
                    });
                    const data = await resp.json();
                    if (!resp.ok) throw new Error(data?.error || 'failed');
                    setMessages(prev => prev.map(msg =>
                      msg.profileProposal?.proposal_id === pid
                        ? { ...msg, proposalStatus: action === 'apply' ? 'applied' : 'declined' }
                        : msg
                    ));
                  } catch {
                    setMessages(prev => prev.map(msg =>
                      msg.profileProposal?.proposal_id === pid ? { ...msg, proposalStatus: 'error' } : msg
                    ));
                  }
                }}
              />
            ) : (
              <div key={i} className={"msg " + m.role}>
                {m.role === 'assistant' && (
                  <div className="msg-header">
                    <span className="msg-avatar">G</span>
                    {m.message_id && !m.streaming && (
                      <>
                        <button
                          className="rating-btn"
                          aria-label={m.rating === 1 ? 'Remove thumbs up' : 'Thumbs up'}
                          aria-pressed={m.rating === 1}
                          title="Helpful"
                          onClick={() => setRating(m.message_id!, i, 1)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 4, marginLeft: 4, display: 'inline-flex',
                            color: m.rating === 1 ? '#2ec486' : 'var(--text-dim)',
                          }}
                        >
                          <ThumbsUp size={14} fill={m.rating === 1 ? '#2ec486' : 'none'} strokeWidth={2} />
                        </button>
                        <button
                          className="rating-btn"
                          aria-label={m.rating === -1 ? 'Remove thumbs down' : 'Thumbs down'}
                          aria-pressed={m.rating === -1}
                          title="Not helpful"
                          onClick={() => setRating(m.message_id!, i, -1)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 4, display: 'inline-flex',
                            color: m.rating === -1 ? '#e74c3c' : 'var(--text-dim)',
                          }}
                        >
                          <ThumbsDown size={14} fill={m.rating === -1 ? '#e74c3c' : 'none'} strokeWidth={2} />
                        </button>
                      </>
                    )}
                    {m.message_id && <button className={"bookmark-btn " + (m.bookmarked ? "active" : "")} onClick={() => toggleBookmark(m.message_id!, i)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={m.bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                    </button>}
                  </div>
                )}
                <div className={"msg-body " + m.role + (m.streaming ? " streaming" : "")} dangerouslySetInnerHTML={{ __html: m.role === 'user' ? m.content.replace(/</g,'&lt;') : formatMarkdown(m.content) }} />

                {/* Summary box — shown when a summary exists */}
                {m.summary && (
                  <div className="summary-box">
                    <div className="summary-header">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="21" y1="10" x2="7" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="7" y2="18" /></svg>
                      <span>Quick Summary</span>
                    </div>
                    <div className="summary-content" dangerouslySetInnerHTML={{ __html: formatMarkdown(m.summary) }} />
                  </div>
                )}

                {/* Summarize button — below answer, above sources, only after streaming completes */}
                {m.role === 'assistant' && m.message_id && !m.streaming && !m.summary && !m.summarizing && (
                  <button className="summarize-action-btn" onClick={() => summarizeMessage(m.message_id!, i)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="21" y1="10" x2="7" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="7" y2="18" /></svg>
                    Summarize
                  </button>
                )}
                {m.summarizing && <span className="summarize-loading">Summarizing...</span>}

                {/* Sources — always last */}
                {m.sources && m.sources.length > 0 && !m.streaming && (
                  <div className="sources-bar">
                    <span className="sources-label">Sources</span>
                    {[...new Set(m.sources.map(s => s.title).filter(Boolean))].map((t, j) => <span key={j} className="source-chip">{t}</span>)}
                  </div>
                )}
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && <div className="msg assistant"><div className="msg-header"><span className="msg-avatar">G</span></div><div className="typing"><span /><span /><span /></div></div>}
          </div>
        )}

        {!online ? (
          <div className="input-area">
            <OfflineMessage feature="AI Coach" />
          </div>
        ) : isPaywalled && messages.length > 0 ? (
          <div className="paywall-bar">
            <div className="paywall-bar-inner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              <span>You've used your 3 free questions.</span>
              <button className="paywall-btn-sm" onClick={() => navigate('/checkout')}>Upgrade to keep asking</button>
            </div>
          </div>
        ) : (
          <div className="input-area">
            <div className="input-row">
              <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} placeholder="" />
              <button className="send-btn" onClick={() => sendMessage()} disabled={isLoading || !input.trim()}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              </button>
            </div>
            {/* The human relief valve (2026-10-06): a real button, stated as
                a capability — never as the AI having failed. Secondary
                styling so the send button stays the main event. */}
            <button
              onClick={() => setShowCoachModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, margin: '10px auto 0',
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 999,
                padding: '7px 16px', color: 'var(--text)', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <UserRound size={15} /> Message a human coach
            </button>
          </div>
        )}
      </div>
      {showCoachModal && (
        <CoachContactModal
          userEmail={session.user.email ?? ''}
          onClose={() => setShowCoachModal(false)}
        />
      )}
    </div>
  );
}
